import {
  Button,
  Checkbox,
  Flex,
  Grid,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Space,
  Text,
  Tooltip,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { InfoOIcon } from '@tonic-one/react-icons';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { QuarantineApi } from '@Apis';
import ErrorAlert from '../../../components/Common/ErrorAlert';
import { QuarantineLogTypeEnum } from '../../../types/log';
import { exportFile } from '../../../utils/common';
import { validatePassword } from '../../../utils/validate';
import {
  QUARANTINE_TYPE_NAME_MAP,
  allowedFileStatusQuarantineDownload,
} from '../utils/constants';
import {
  CloudAppQuarantineDownloadActionReqDto,
  QuarantineDownloadActionReqDto,
} from '../utils/types';
import { QuarantineItemModalName, QuarantineItemModalState } from './interface';
import {
  base64ToString,
  getCustomizedName,
  getQuarantineDownloadActionURL,
} from '../utils/fns';

export function DownloadModal({
  quarantineType,
  modal,
  setModal,
}: {
  quarantineType: QuarantineLogTypeEnum;
  modal: QuarantineItemModalState;
  setModal: ({ name, quarantineItems }: QuarantineItemModalState) => void;
}) {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });

  const [enableEncrypt, setEnableEncrypt] = useState(false);
  const passwordRef = useRef<HTMLInputElement | null>(null);
  const passwordConfirmRef = useRef<HTMLInputElement | null>(null);

  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);
  const { t } = useTranslation();

  //check show encrypt or not
  //if one need encrypt, encrypt all
  let isShowEncrypt = false;
  if (
    quarantineType === QuarantineLogTypeEnum.SharePoint ||
    quarantineType === QuarantineLogTypeEnum.OneDrive ||
    quarantineType === QuarantineLogTypeEnum.Exchange ||
    quarantineType === QuarantineLogTypeEnum.Teams
  ) {
    for (const _item of modal.quarantineItems) {
      if (_item.filterType === 'Malware Scanning') {
        if (_item.blockReason) {
          const reasonString = _item.blockReason.join(';');
          if (
            !reasonString.includes('Over restriction') &&
            !reasonString.includes('Protected file') &&
            !reasonString.includes('Other protected file') &&
            !reasonString.includes('Protected compressed file')
          ) {
            isShowEncrypt = true;
            break;
          }
        }
      } else if (_item.filterType === 'Virtual Analyzer') {
        if (
          _item.ddaaasRiskLevel &&
          ['High Risk', 'Medium Risk', 'Low Risk'].includes(
            _item.ddaaasRiskLevel
          )
        ) {
          isShowEncrypt = true;
          break;
        }
      }
    }
  }

  if (modal.name !== QuarantineItemModalName.Download) {
    return <></>;
  }

  const onClose = () => {
    setModal({
      name: QuarantineItemModalName.None,
      quarantineItems: modal.quarantineItems,
    });
  };

  const isStorageDownload = ![
    QuarantineLogTypeEnum.Exchange,
    QuarantineLogTypeEnum.SharePoint,
    QuarantineLogTypeEnum.OneDrive,
    QuarantineLogTypeEnum.Teams,
    QuarantineLogTypeEnum.ExchangeInline,
  ].includes(quarantineType);

  const onClick = async () => {
    if (isShowEncrypt && enableEncrypt) {
      if (!validatePassword(passwordRef.current?.value as string)) {
        setErrorMsg(t('quarantine.modal.download.complexityWarning'));
        return;
      } else if (
        passwordRef.current?.value !== passwordConfirmRef.current?.value
      ) {
        setErrorMsg(t('quarantine.modal.download.matchWarning'));
        return;
      }
    }

    const url = getQuarantineDownloadActionURL(quarantineType);

    if (url === 'cloudApp/createLink') {
      //just one item
      const data: CloudAppQuarantineDownloadActionReqDto = {
        serviceName: QUARANTINE_TYPE_NAME_MAP.get(quarantineType) as string,
        companyGuid: modal.quarantineItems[0].companyGuid,
        uniqueId: modal.quarantineItems[0].uniqueId,
      };

      try {
        const link =
          (await QuarantineApi.apiDownloadCloudAppQuarantineLogs(url, data))
            .data?.data || '';
        window.open(link, '_blank');
        setModal({
          name: QuarantineItemModalName.None,
          quarantineItems: modal.quarantineItems,
        });
      } catch {
        window.showNotification({
          titleMsg: t('common.apiException.internalError'),
          severity: 'error',
        });
      }
    } else {
      const data: QuarantineDownloadActionReqDto = {
        itemList: [],
      };
      if (url === 'exchangeOnline/download') {
        modal.quarantineItems.forEach((_item) => {
          //Only Quarantined file can be download
          if (
            allowedFileStatusQuarantineDownload.includes(_item.fileStatusKey)
          ) {
            data.itemList.push({
              companyGuid: _item.companyGuid,
              emailAddress: _item.violator,
              traceId: _item.traceId,
              uniqueId: _item.uniqueId,
              uniqueLogId: _item.uniqueLogId,
              violator: _item.violator || '',
            });
          }
        });
        data.password = enableEncrypt ? passwordRef.current?.value : '';
      } else if (url === 'spod/download') {
        modal.quarantineItems.forEach((_item) => {
          if (
            allowedFileStatusQuarantineDownload.includes(_item.fileStatusKey)
          ) {
            data.itemList.push({
              companyGuid: _item.companyGuid,
              traceId: _item.traceId,
              uniqueId: _item.uniqueId || '',
              uniqueLogId: _item.uniqueLogId,
              violator: _item.violator || '',

              siteUrl: _item.siteUrl,
              graphSiteId: _item.graphSiteId,
              useSpodtmDelegator: _item.useSpodtmDelegator || 'false',

              originalDriveId: _item.originalDriveId || '',
              originalParentId: _item.originalParentId || '',
              originalId: _item.originalId || '',
              fileName: (_item as any).fileNamePath[_item.uniqueId],
              fileOriginalPath: _item.fileOriginalPath || '',

              quarantineDriveId: _item.quarantineDriveId || '',
              quarantineParentId: _item.quarantineParentId || '',
              quarantineItemId: _item.quarantineItemId || '',
              quarantineFileName: _item.quarantineFileName || '',
              notificationFileId: _item.notificationFileId || '',
            });
          }
        });
        data.password = enableEncrypt ? passwordRef.current?.value : '';
        data.serviceName = QUARANTINE_TYPE_NAME_MAP.get(
          quarantineType
        ) as string;
      } else if (url === 'gmail/download') {
        modal.quarantineItems.forEach((_item) => {
          if (
            allowedFileStatusQuarantineDownload.includes(_item.fileStatusKey)
          ) {
            data.itemList.push({
              logDocId: _item.id,
              logId: _item.uniqueLogId,
              logIndexId: _item.index,
              logType: _item.type, //email_address
              uniqueId: _item.uniqueId,
              violator: _item.violator || '',
              messageId: _item.messageId,
              companyGuid: _item.companyGuid,
              dedicatedMailbox: _item.msgDedicatedQuarantineMailbox,
            });
          }
        });
      } else if (url === 'inlineMail/download') {
        modal.quarantineItems.forEach((_item) => {
          if (
            allowedFileStatusQuarantineDownload.includes(_item.fileStatusKey)
          ) {
            data.itemList.push({
              uniqueId: _item.uniqueId,
              backupBlobPath: _item.backupBlobPath,
              blobPath: _item.blobPath || '',
              companyGuid: _item.companyGuid,
              deliveryDateTime: _item.msgDeliveryTime,
              direction: _item.mailDirectionKey,
              emailAddress: _item.violator,
              esLogId: _item.id,
              esLogIndex: _item.index,
              esLogType: _item.type,
              fileName: _item.msgSubject,
              traceId: _item.traceId,
              uniqueLogId: _item.uniqueLogId,
              violator: _item.violator,
            });
          }
        });
        data.password = enableEncrypt ? passwordRef.current?.value : '';
        data.serviceName = QUARANTINE_TYPE_NAME_MAP.get(
          quarantineType
        ) as string;
      } else if (url === 'teams/download') {
        modal.quarantineItems.forEach((_item) => {
          if (
            allowedFileStatusQuarantineDownload.includes(_item.fileStatusKey)
          ) {
            data.itemList.push({
              uniqueId: _item.uniqueId || '',
              uniqueLogId: _item.uniqueLogId,
              traceId: _item.traceId,
              violator: _item.violator || '',
              dedicatedSite: _item.dedicatedSite || '',
              companyGuid: _item.companyGuid,

              siteUrl: _item.siteUrl,
              graphSiteId: _item.graphSiteId,
              useSpodtmDelegator: _item.useSpodtmDelegator || 'false',

              originalDriveId: _item.originalDriveId || '',
              originalParentId: _item.originalParentId || '',
              originalId: _item.originalId || '',
              fileName: _item.fileName,
              fileOriginalPath: _item.fileOriginalPath || '',

              quarantineDriveId: _item.quarantineDriveId || '',
              quarantineParentId: _item.quarantineParentId || '',
              quarantineItemId: _item.quarantineItemId || '',
              quarantineFileName: _item.quarantineFileName || '',
              notificationFileId: _item.notificationFileId || '',
            });
          }
        });
        data.password = enableEncrypt ? passwordRef.current?.value : '';
      }
      try {
        const res = await QuarantineApi.apiDownloadQuarantineLogs(url, data);
        const disposition = res.headers['content-disposition'];
        if (disposition) {
          const downloadInfo = JSON.parse(
            base64ToString(res.headers['download-info'])
          );
          if (downloadInfo.name !== null && downloadInfo.violator !== null) {
            const fileName = getCustomizedName(
              downloadInfo.name,
              downloadInfo.violator
            );
            exportFile(fileName, res.data as any, 'application/octet-stream');
          }
        }
        setModal({
          name: QuarantineItemModalName.None,
          quarantineItems: modal.quarantineItems,
        });
      } catch {
        window.showNotification({
          titleMsg: t('common.apiException.internalError'),
          severity: 'error',
        });
      }
    }
  };

  return (
    <Modal
      closeOnEsc
      isClosable
      isOpen={modal.name === QuarantineItemModalName.Download}
      onClose={onClose}
      size="sm"
    >
      <ModalOverlay />
      <ModalContent width={480}>
        <ModalBody>
          {errorMsg && (
            <ErrorAlert
              errorMsg={errorMsg}
              onClose={() => setErrorMsg(undefined)}
            />
          )}
          <Text mb="4x">{t('quarantine.modal.download.desc')}</Text>
          {isStorageDownload && (
            <Flex gap="8px" align="center">
              <Icon as={InfoOIcon} color={colorStyle.color.info} />
              <Text fontSize="semibold">{t('common.label.note')}:</Text>
              <Text>{t('quarantine.modal.download.allowPopUp')}</Text>
            </Flex>
          )}

          {isShowEncrypt && (
            <Checkbox
              checked={enableEncrypt}
              onChange={(e: any) => {
                setEnableEncrypt(e.target.checked);
              }}
            >
              {t('quarantine.modal.download.encrypt')}
            </Checkbox>
          )}

          {enableEncrypt && (
            <>
              <Grid
                templateColumns="max-content auto"
                columnGap="3x"
                rowGap="3x"
                pt="12px"
              >
                <Text pt="8px">{t('common.label.password')}:</Text>
                <Tooltip
                  label={
                    <Text
                      dangerouslySetInnerHTML={{
                        __html: t('quarantine.modal.download.passwordRule'),
                      }}
                    />
                  }
                  PopperProps={{ usePortal: true }}
                  backgroundColor={colorStyle.background.tertiary}
                  color={colorStyle.color.primary}
                  maxWidth={250}
                  p="8px 12px"
                >
                  <Input
                    placeholder={t('common.label.password')}
                    type="password"
                    maxWidth={250}
                    ref={passwordRef}
                  />
                </Tooltip>
                <Text pt="8px">{t('common.label.confirmPassword')}</Text>
                <Input
                  placeholder={t('common.label.password')}
                  type="password"
                  maxWidth={250}
                  ref={passwordConfirmRef}
                />
              </Grid>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="primary" minWidth="20x" onClick={onClick}>
            {t('common.button.ok')}
          </Button>
          <Space width="2x" />
          <Button minWidth="20x" onClick={onClose}>
            {t('common.button.cancel')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
